import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import {
  Meeting,
  MeetingResponse,
  OOOItem,
  TSessionCheckInTime,
} from '../types/timeline';

/**
 * Calculates duration in minutes between two timestamps
 */
export function calculateDurationInMinutes(
  startTime: string,
  endTime: string,
): number {
  return Math.round(
    (new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60),
  );
}

/**
 * Transforms a meeting response into a Meeting type with calculated duration
 */
export function transformMeeting(meeting: MeetingResponse): Meeting {
  return {
    id: meeting.id,
    title: meeting.title,
    start_time: meeting.start_time,
    end_time: meeting.end_time,
    duration_minutes: calculateDurationInMinutes(
      meeting.start_time,
      meeting.end_time,
    ),
  };
}

/**
 * Calculates total duration of multiple meetings
 */
export function calculateTotalMeetingDuration(
  meetings: MeetingResponse[],
): number {
  return meetings.reduce(
    (total, meeting) =>
      total + calculateDurationInMinutes(meeting.start_time, meeting.end_time),
    0,
  );
}

/**
 * Safely extracts member IDs from team members data
 */
export function extractMemberIds(
  teamMembers: { id: string }[] | undefined,
): string[] {
  return teamMembers?.map((member) => member.id) || [];
}

export const isSessionEnded = (
  checkInTime: TSessionCheckInTime,
  checkWithDelay: boolean,
): boolean => {
  const currentDate = new Date();
  const isToday =
    checkInTime.date.getFullYear() === currentDate.getFullYear() &&
    checkInTime.date.getMonth() === currentDate.getMonth() &&
    checkInTime.date.getDate() === currentDate.getDate();
  if (!isToday) {
    return true;
  }

  const [time, period] = checkInTime.checkInTime.split(' ');
  const [hours, minutes] = time.split(':').map(Number);

  const checkInDate = new Date(
    checkInTime.date.getFullYear(),
    checkInTime.date.getMonth(),
    checkInTime.date.getDate(),
    period === 'PM' && hours !== 12 ? hours + 12 : hours,
    minutes,
    0,
    0,
  );

  const currentTime = new Date();
  if (checkWithDelay) {
    const oneHourLater = new Date(checkInDate.getTime() + 60 * 60 * 1000);
    return currentTime > oneHourLater;
  }
  return currentTime > checkInDate;
};

export const getOOOItem = (memberId: string): [OOOItem] => {
  return [
    {
      id: memberId,
      type: 'OOO',
      reason: 'out of office',
    },
  ];
};

/**
 * Use this function when you need to handle time-specific operations or display times
 * in the team's timezone (IST). This is primarily for time strings and UTC timestamps.
 */
export const getTimeInTeamTimezone = (input: string | Date): Date => {
  if (!input) {
    return new Date();
  }

  // For Date objects, return as is to prevent timezone issues
  if (input instanceof Date) {
    return new Date(input);
  }

  // Handle ISO date string with timezone (e.g., "2025-04-18T09:30:00+00:00")
  if (input.includes('T') && input.includes('+')) {
    const date = parseISO(input);
    return toZonedTime(date, 'Asia/Kolkata');
  }

  // For time strings, create a new date with the specified time
  const normalizedTimeStr = input.trim().toUpperCase();
  const date = new Date();

  // Handle 12-hour format (e.g., "12:30 PM", "12:30PM", "12:30 pm")
  if (normalizedTimeStr.includes('AM') || normalizedTimeStr.includes('PM')) {
    const [time, period] = normalizedTimeStr.split(/\s+/);
    const [hoursStr, minutesStr] = time.split(':');

    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 12 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return new Date();
    }

    let hours24 = hours;
    if (period === 'PM' && hours !== 12) {
      hours24 = hours + 12;
    } else if (period === 'AM' && hours === 12) {
      hours24 = 0;
    }

    date.setHours(hours24, minutes, 0, 0);
    return date;
  }

  // Handle 24-hour format (e.g., "09:30", "9:30")
  const [hoursStr, minutesStr] = normalizedTimeStr.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return new Date();
  }

  date.setHours(hours, minutes, 0, 0);
  return date;
};

/**
 * Use this function when you need to handle date-only operations (like date comparisons,
 * date intervals, etc.) without timezone conversion.
 */
export const getDateWithoutTimezone = (input: string | Date): Date => {
  if (!input) {
    return new Date();
  }

  if (input instanceof Date) {
    return new Date(input);
  }

  if (input.includes('T')) {
    return parseISO(input);
  }

  return new Date(input);
};
